import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DefinitionList, Dialog, ErrorState, Field, Loading, Metric, Textarea,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { date, humanise, money, number, phoneWithCountryCode } from '../../lib/format.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';

/**
 * One listing, with everything a review decision rests on.
 *
 * The table can carry a title, a price and a status; it cannot carry the
 * description, the photographs or who the landlord is, which is what an admin
 * is actually judging. Approving is possible from either place — from the row
 * when the decision is obvious, from here when it is not.
 */

/** What each decision does to the listing, for the dialog's warning. */
const DECISION_WARNINGS = {
  reject:
    'The listing is marked rejected and never goes live. The broker sees the reason you give here. They can still edit it, but editing does not send it back for review. Recorded in the audit log against your account.',
  suspend:
    'The listing comes down from search straight away and stops taking enquiries. Ad time already paid for is not refunded, and there is no reinstate button on this page. Recorded in the audit log against your account.',
};

/** The full address, written the way somebody would say it aloud. */
function addressLines(address = {}) {
  return [
    address.line1,
    address.line2,
    [address.locality, address.city].filter(Boolean).join(', '),
    address.state,
    address.country,
  ].filter(Boolean);
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canModerate = can(PERMISSIONS.BROKER_PROPERTY_MODERATE);

  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const query = useQuery({
    queryKey: ['admin-broker-property', id],
    queryFn: () => api.broker.property(id),
  });

  const moderate = useMutation({
    mutationFn: ({ decision: verdict, reason: why }) =>
      api.broker.moderateProperty(id, { decision: verdict, ...(why ? { reason: why } : {}) }),
    onSuccess: (_result, variables) => {
      toast.success(
        {
          approve: 'Listing approved. It is live now.',
          publish: 'Draft published. The listing is live.',
          reject: 'Listing rejected.',
        }[variables.decision] || 'Listing suspended.',
      );
      setDecision(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['admin-broker-property', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-broker-properties'] });
    },
    onError: (error) => {
      // An incomplete draft comes back with the fields still missing; name them.
      const missing = Object.keys(error.details?.fields || {});
      toast.error(
        missing.length
          ? `${error.message} Missing or invalid: ${missing.map((field) => field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\./g, ' ').toLowerCase()).join(', ')}.`
          : error.message || 'That decision could not be recorded.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (why) => api.broker.removeProperty(id, why),
    onSuccess: () => {
      toast.success('Listing deleted.');
      queryClient.invalidateQueries({ queryKey: ['admin-broker-properties'] });
      queryClient.removeQueries({ queryKey: ['admin-broker-property', id] });
      // The listing has gone from every list; there is nothing left to show here.
      navigate('/inventory');
    },
    onError: (error) => toast.error(error.message || 'That listing could not be deleted.'),
  });

  const closeDelete = () => {
    setDeleting(false);
    setDeleteReason('');
  };

  if (query.isLoading) return <Loading label="Opening the listing" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const listing = query.data;
  const photos = listing.photoUrls?.length ? listing.photoUrls : [listing.coverPhotoUrl].filter(Boolean);
  const waiting = listing.status === 'pending_verification';

  return (
    <Page
      title={listing.title || 'Untitled draft'}
      subtitle={addressLines(listing.address).join(' · ')}
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/inventory')}>
            Back to inventory
          </Button>
          {canModerate && waiting && (
            <>
              <Button
                onClick={() => moderate.mutate({ decision: 'approve' })}
                disabled={moderate.isPending}
              >
                Approve
              </Button>
              <Button variant="ghost" onClick={() => setDecision('reject')}>
                Reject
              </Button>
            </>
          )}
          {canModerate && listing.status === 'draft' && (
            <Button
              onClick={() => moderate.mutate({ decision: 'publish' })}
              disabled={moderate.isPending}
            >
              Publish draft
            </Button>
          )}
          {canModerate && listing.status === 'active' && (
            <Button variant="danger" onClick={() => setDecision('suspend')}>
              Suspend
            </Button>
          )}
          {canModerate && (
            <Button variant="ghost" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
        </>
      }
    >
      <div className="metric-grid">
        <Metric label="Rent" value={money(listing.rentMinor)} caption="per month" />
        <Metric label="Deposit" value={money(listing.depositMinor)} />
        <Metric label="Status" value={<Badge status={listing.status} />} />
        <Metric label="Views" value={number(listing.stats?.viewCount || 0)} />
        <Metric label="Unique viewers" value={number(listing.stats?.uniqueViewCount || 0)} />
      </div>

      <Card title="The listing">
        <p className="listing-description">{listing.description}</p>
      </Card>

      {photos.length > 0 && (
        <Card title={`Photographs (${photos.length})`}>
          <div className="photo-grid">
            {photos.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer">
                <img src={url} alt="" loading="lazy" />
              </a>
            ))}
          </div>
        </Card>
      )}

      <Card title="The property">
        <DefinitionList
          items={[
            { label: 'Type', value: humanise(listing.propertyType) },
            { label: 'Bedrooms', value: listing.bedrooms },
            { label: 'Bathrooms', value: listing.bathrooms },
            { label: 'Balconies', value: listing.balconies },
            { label: 'Area', value: listing.areaSqft ? `${number(listing.areaSqft)} sq ft` : null },
            {
              label: 'Floor',
              value:
                listing.floor != null
                  ? `${listing.floor}${listing.totalFloors ? ` of ${listing.totalFloors}` : ''}`
                  : null,
            },
            { label: 'Furnishing', value: humanise(listing.furnishing) },
            { label: 'Facing', value: listing.facing },
            { label: 'Age', value: listing.ageYears != null ? `${listing.ageYears} years` : null },
            { label: 'Amenities', value: listing.amenities?.join(', ') },
          ]}
        />
      </Card>

      <Card title="Terms">
        <DefinitionList
          items={[
            { label: 'Available from', value: listing.availableFrom },
            { label: 'Minimum lease', value: listing.minimumLeaseMonths != null ? `${listing.minimumLeaseMonths} months` : null },
            { label: 'Deposit', value: listing.depositMonths ? `${listing.depositMonths} months` : null },
            { label: 'Brokerage', value: listing.brokerageMinor != null ? money(listing.brokerageMinor) : null },
            { label: 'Maintenance', value: money(listing.maintenanceMinor) },
            { label: 'Negotiable', value: listing.negotiable ? 'Yes' : 'No' },
            { label: 'Pets', value: listing.petsAllowed ? 'Allowed' : 'Not allowed' },
            { label: 'Preferred tenants', value: listing.preferredTenantTypes?.map(humanise).join(', ') },
          ]}
        />
      </Card>

      <Card title="Who listed it">
        <DefinitionList
          items={[
            {
              label: 'Agency',
              value: listing.broker ? (
                <Link to={`/brokers/${listing.broker._id}`}>{listing.broker.businessName}</Link>
              ) : (
                'Owner, no agency'
              ),
            },
            { label: 'Agency verification', value: listing.broker && <Badge status={listing.broker.verification?.status} /> },
            { label: 'Filed by', value: listing.brokerUser?.name },
            { label: 'Contact', value: phoneWithCountryCode(listing.brokerUser?.phone) },
            /* The landlord's own details, which the public listing never shows. */
            { label: 'Landlord', value: listing.owner?.name },
            { label: 'Landlord phone', value: phoneWithCountryCode(listing.owner?.phone) },
            { label: 'Internal reference', value: listing.owner?.internalReference },
          ]}
        />
      </Card>

      <Card title="Review history">
        <DefinitionList
          items={[
            { label: 'Verification', value: <Badge status={listing.verification?.status} /> },
            { label: 'Reviewed', value: listing.verification?.reviewedAt && date(listing.verification.reviewedAt, { withTime: true }) },
            { label: 'Rejected because', value: listing.verification?.rejectionReason },
            { label: 'Suspended because', value: listing.suspension?.reason },
            { label: 'Filed', value: date(listing.createdAt, { withTime: true }) },
            { label: 'Published', value: listing.publishedAt && date(listing.publishedAt, { withTime: true }) },
            { label: 'Ad paid until', value: listing.ad?.paidUntil && date(listing.ad.paidUntil) },
          ]}
        />
      </Card>

      <Dialog
        open={Boolean(decision)}
        onClose={() => {
          setDecision(null);
          setReason('');
        }}
        title={decision === 'reject' ? 'Reject this listing' : 'Suspend this listing'}
        warning={DECISION_WARNINGS[decision]}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDecision(null);
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3 || moderate.isPending}
              onClick={() => moderate.mutate({ decision, reason: reason.trim() })}
            >
              {decision === 'reject' ? 'Reject' : 'Suspend'}
            </Button>
          </>
        }
      >
        <Field
          label="Reason"
          hint="Required. Kept with the decision, and what the broker is told. Be specific enough to act on."
        >
          <Textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Photographs do not match the address given"
          />
        </Field>
      </Dialog>

      <Dialog
        open={deleting}
        onClose={closeDelete}
        title={`Delete ${listing.title}?`}
        warning="The listing comes down from search and off every list here, and the broker can no longer see, edit or resume it. Its leads, enquiries and deals stay, because those records point at it. Any ad time already paid for is not refunded. Not reversible from the panel."
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              disabled={deleteReason.trim().length < 3}
              onClick={() => remove.mutate(deleteReason.trim())}
            >
              Delete listing
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Required. Recorded in the audit log against your account.">
          <Textarea
            rows={3}
            value={deleteReason}
            onChange={(event) => setDeleteReason(event.target.value)}
            placeholder="Duplicate of another listing by the same agency"
          />
        </Field>
      </Dialog>
    </Page>
  );
}
